import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  LogOut,
  UserPlus,
  Users,
  DollarSign,
  HandCoins,
  Wallet,
  Shield,
  Lock,
  Table,
  List,
  Key,
  Settings,
  TrendingUp,
  AlertTriangle,
  Crown,
  Activity,
  Zap,
} from 'lucide-react';

interface SidebarProps {
  currentSection: string;
  onLogout: () => void;
}

const menuGroups = [
  {
    title: 'User Management',
    items: [
      { id: 'create-user', label: 'Create User', icon: UserPlus },
      { id: 'manage-users', label: 'Manage Users', icon: Users },
      { id: 'verification-requests', label: 'Verification Requests', icon: Shield },
    ],
  },
  {
    title: 'Manager & Admin',
    items: [
      { id: 'create-manager', label: 'Create Manager', icon: Users },
      { id: 'manage-managers', label: 'Manage Managers', icon: Shield },
      { id: 'manage-admins', label: 'Manage Admins', icon: Lock },
      { id: 'create-admin', label: 'Create Admin (Super Only)', icon: Crown },
    ],
  },
  {
    title: 'Credits & Cashouts',
    items: [
      { id: 'account', label: 'Account', icon: Wallet },
      { id: 'add-credits', label: 'Add/Remove Credits', icon: DollarSign },
      { id: 'assign-manager-credits', label: 'Assign Manager Credits', icon: HandCoins },
      { id: 'cashouts', label: 'Cashouts', icon: Wallet },
      { id: 'credit-distribution-history', label: 'Credits Distribution Log', icon: TrendingUp },
    ],
  },
  {
    title: 'Tables & RTP Control',
    items: [
      { id: 'create-table', label: 'Create Table', icon: Table },
      { id: 'manage-tables', label: 'Manage Tables', icon: List },
      { id: 'rtp-control', label: 'RTP Settings', icon: Settings },
      { id: 'global-rtp', label: 'Global RTP (Auto Tables)', icon: Zap },
      { id: 'volatility-control', label: 'Volatility Control', icon: Activity },
      { id: 'time-rtp-schedule', label: 'Time RTP Schedule', icon: TrendingUp },
      { id: 'player-lifetime-rtp', label: 'Player Lifetime RTP', icon: Crown },
    ],
  },
  {
    title: 'Security & Logs',
    items: [
      { id: 'game-monitor', label: 'Game Monitor', icon: Activity },
      { id: 'compliance-logs', label: 'Compliance Logs', icon: AlertTriangle },
      { id: 'change-password', label: 'Change Password', icon: Key },
    ],
  },
];

export default function Sidebar({ currentSection, onLogout }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('adminSidebarCollapsed') === 'true');
  const adminInfoRaw = localStorage.getItem('adminInfo');
  const isSuperAdmin = adminInfoRaw ? !!JSON.parse(adminInfoRaw).is_super_admin : false;

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', String(collapsed));
  }, [collapsed]);

  return (
    <div className={`bg-indigo-900 text-white h-full transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}>
      <div className="flex justify-between items-center p-4 border-b border-indigo-700">
        {!collapsed && <h2 className="text-lg font-bold">Admin Panel</h2>}
        <button
          className="bf-icon-gloss rounded-lg p-2 text-white hover:text-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Menu size={24} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        {menuGroups.map((group) => (
          <div key={group.title} className="mb-6">
            {!collapsed && (
              <h5 className="px-4 text-xs font-semibold text-indigo-300 uppercase tracking-wider mb-2">
                {group.title}
              </h5>
            )}
            {group.items
              .filter((item) => isSuperAdmin || item.id !== 'create-admin')
              .map((item) => (
                <Link
                  key={item.id}
                  to={`/admin/dashboard?section=${item.id}`}
                  className={`flex items-center px-4 py-3 text-sm hover:bg-indigo-800 transition-colors ${
                    currentSection === item.id ? 'bg-indigo-800 border-l-4 border-indigo-400 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]' : ''
                  }`}
                  aria-current={currentSection === item.id ? 'page' : undefined}
                >
                  <span className={`${collapsed ? 'mx-auto' : 'mr-3'} text-indigo-300`}>
                    <item.icon size={20} />
                  </span>
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              ))}
          </div>
        ))}

        <button
          className="bf-glossy-danger flex items-center w-full px-4 py-3 text-sm transition-colors mt-auto border-t border-indigo-700"
          onClick={onLogout}
          aria-label="Logout"
        >
          <span className={`${collapsed ? 'mx-auto' : 'mr-3'} text-red-300`}>
            <LogOut size={20} />
          </span>
          {!collapsed && <span className="text-red-300">Logout</span>}
        </button>
      </nav>

      {!collapsed && (
        <div className="p-4 text-xs text-indigo-400 border-t border-indigo-700">
          Bolsa Fish Admin v1.0
        </div>
      )}
    </div>
  );
}
